import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import {
  DEFAULT_LOAN_CONFIG,
  buildLoanAmountSteps,
  calculateLoanTerms,
  pickDefaultLoanAmount,
} from "../../../infrastructure/utils/loanCalculator";
import { formatCurrency, formatNumber } from "../../../infrastructure/utils/formatters";
import LoanScrollPicker from "./LoanScrollPicker";

const DetailRow = ({ label, value, emphasize = false }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={2}
    sx={{ py: 1.25 }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant={emphasize ? "subtitle1" : "body2"}
      fontWeight={emphasize ? 700 : 600}
      color={emphasize ? "primary.main" : "text.primary"}
      textAlign="left"
    >
      {value}
    </Typography>
  </Stack>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  emphasize: PropTypes.bool,
};

const LoanRequestSimulator = ({
  title = "وام پنل خورشیدی",
  maxLoanAmount,
  loanAmount,
  installmentCount,
  onLoanAmountChange,
  onInstallmentCountChange,
  config = DEFAULT_LOAN_CONFIG,
  compact = false,
}) => {
  const amountSteps = useMemo(
    () =>
      buildLoanAmountSteps(
        config.minAmount,
        maxLoanAmount || config.maxAmount,
        config.amountStep
      ),
    [config.amountStep, config.maxAmount, config.minAmount, maxLoanAmount]
  );

  const [internalAmount, setInternalAmount] = useState(() =>
    pickDefaultLoanAmount(maxLoanAmount || config.maxAmount, config)
  );
  const [internalInstallments, setInternalInstallments] = useState(
    config.defaultInstallments
  );

  const activeAmount = loanAmount ?? internalAmount;
  const activeInstallments = installmentCount ?? internalInstallments;

  useEffect(() => {
    if (loanAmount != null || !maxLoanAmount) return;
    const next = pickDefaultLoanAmount(maxLoanAmount, config);
    setInternalAmount(next);
    onLoanAmountChange?.(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, loanAmount, maxLoanAmount]);

  useEffect(() => {
    if (!amountSteps.length) return;
    if (amountSteps.includes(activeAmount)) return;
    const fallback = amountSteps[amountSteps.length - 1] ?? config.minAmount;
    if (loanAmount == null) {
      setInternalAmount(fallback);
    }
    onLoanAmountChange?.(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAmount, amountSteps, config.minAmount, loanAmount, maxLoanAmount]);

  const terms = useMemo(
    () =>
      calculateLoanTerms(
        activeAmount,
        config.annualInterestRate,
        activeInstallments
      ),
    [activeAmount, activeInstallments, config.annualInterestRate]
  );

  const handleAmountChange = (nextAmount) => {
    if (loanAmount == null) {
      setInternalAmount(nextAmount);
    }
    onLoanAmountChange?.(nextAmount);
  };

  const handleInstallmentChange = (nextCount) => {
    if (installmentCount == null) {
      setInternalInstallments(nextCount);
    }
    onInstallmentCountChange?.(nextCount);
  };

  const facilityCeiling = maxLoanAmount || config.maxAmount;

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {title} ({formatCurrency(facilityCeiling)})
        </Typography>
        <IconButton size="small" aria-label="اشتراک‌گذاری" disabled>
          <ShareOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <CardContent sx={{ pt: 0, px: compact ? 2 : 3, pb: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              مبلغ وام را با اسکرول انتخاب کنید
            </Typography>
            <LoanScrollPicker
              ariaLabel="انتخاب مبلغ وام"
              options={amountSteps}
              value={activeAmount}
              onChange={handleAmountChange}
              formatLabel={(amount) => formatCurrency(amount)}
            />
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              تعداد اقساط
            </Typography>
            <LoanScrollPicker
              ariaLabel="انتخاب تعداد اقساط"
              options={config.installmentOptions}
              value={activeInstallments}
              onChange={handleInstallmentChange}
              formatLabel={(count) => `${formatNumber(count)} قسط`}
            />
          </Box>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2.5,
              px: 2,
              bgcolor: "grey.50",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ pt: 2, pb: 1 }}>
              جزئیات مبلغ و اقساط
            </Typography>
            <Divider />
            <DetailRow label="مبلغ درخواستی" value={formatCurrency(activeAmount)} />
            <DetailRow
              label="تعداد اقساط"
              value={`${formatNumber(activeInstallments)} قسط`}
            />
            <DetailRow
              label="مبلغ هر قسط"
              value={formatCurrency(terms.monthlyInstallment)}
            />
            <DetailRow
              label="سود تسهیلات"
              value={formatCurrency(terms.totalInterest)}
            />
            <DetailRow
              label={`سود ${formatNumber(config.annualInterestRate)}٪ سالانه بانکی`}
              value={`${formatNumber(config.annualInterestRate)}٪`}
            />
            <Divider sx={{ my: 0.5 }} />
            <DetailRow
              label="بازپرداخت نهایی"
              value={formatCurrency(terms.totalRepayment)}
              emphasize
            />
          </Box>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              حق اشتراک
            </Typography>
            <DetailRow
              label="نوع"
              value="پرداخت آنلاین هزینه اشتراک"
            />
            <DetailRow
              label="مبلغ"
              value={formatCurrency(config.subscriptionFee)}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              این هزینه شامل تشکیل پرونده و استفاده از تمامی خدمات می‌باشد.
            </Typography>
          </Box>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              جزئیات بازپرداخت
            </Typography>
            <DetailRow label="روش بازپرداخت" value={config.repaymentMethodLabel} />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

LoanRequestSimulator.propTypes = {
  title: PropTypes.string,
  maxLoanAmount: PropTypes.number,
  loanAmount: PropTypes.number,
  installmentCount: PropTypes.number,
  onLoanAmountChange: PropTypes.func,
  onInstallmentCountChange: PropTypes.func,
  config: PropTypes.object,
  compact: PropTypes.bool,
};

export default LoanRequestSimulator;
